import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import Icon from '../AppIcon';
import { useUserAccess } from '../../context/UserAccessContext';
import { useHubupCommunitiesOptional } from '../../context/HubupCommunitiesContext';
import { useCompaniesOptional } from '../../context/CompanyContext';
import {
  canOpenEnterpriseApp,
  getDefaultEnterprisePath,
  getDefaultWebCreatorPath,
  hasMultipleApps,
} from '../../access/userAccess';
import { canManageHubupCommunitySettings } from '../../utils/hubupAccess';
import JustOnePageLogo from '../../pages/web-creator/components/JustOnePageLogo';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [connectionStatus] = useState('connected');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showCommunityMenu, setShowCommunityMenu] = useState(false);
  const [communitySearch, setCommunitySearch] = useState('');
  const communitySearchRef = useRef(null);
  const [showCompanyMenu, setShowCompanyMenu] = useState(false);
  const [showAdminMenu, setShowAdminMenu] = useState(false);
  const companyMenuRef = useRef(null);
  const communityMenuRef = useRef(null);
  const userMenuRef = useRef(null);
  const adminMenuRef = useRef(null);
  const { user: currentUser, profile, activeApp, chooserOpen, openAppChooser, chooseAppMode } = useUserAccess();
  const hubupCx = useHubupCommunitiesOptional();
  const companyCx = useCompaniesOptional();

  const onHubupUrl = location.pathname.startsWith('/hubup');
  const onWebCreatorUrl = location.pathname.startsWith('/web-creator');
  const onEnterpriseUrl = location.pathname.startsWith('/enterprise-manage');
  const onPublicLp = location.pathname.startsWith('/lp/');
  const inWebCreator = onWebCreatorUrl;

  if (onPublicLp) return null;

  const inHubupShell =
    !inWebCreator &&
    (onHubupUrl ||
      profile?.kind === 'hubup_only' ||
      (profile?.kind === 'dual' && activeApp === 'hubup'));

  const groupId =
    params.groupId ||
    location.pathname.match(/\/hubup\/g\/([^/]+)/)?.[1];
  const hubupCommunities = hubupCx?.communities ?? [];

  const filteredHubupCommunities = useMemo(() => {
    const q = communitySearch.trim().toLowerCase();
    if (!q) return hubupCommunities;
    return hubupCommunities.filter((c) => {
      const name = String(c.name ?? '').toLowerCase();
      const slug = String(c.slug ?? '').toLowerCase();
      return name.includes(q) || slug.includes(q);
    });
  }, [hubupCommunities, communitySearch]);

  useEffect(() => {
    if (!showCommunityMenu && !isMobileMenuOpen) {
      setCommunitySearch('');
    }
  }, [showCommunityMenu, isMobileMenuOpen]);

  useEffect(() => {
    if (!showCommunityMenu) return undefined;
    const id = requestAnimationFrame(() => {
      communitySearchRef.current?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [showCommunityMenu]);

  const closeHeaderMenus = useCallback(() => {
    setShowCompanyMenu(false);
    setShowCommunityMenu(false);
    setShowUserMenu(false);
    setShowAdminMenu(false);
  }, []);

  const anyHeaderMenuOpen =
    showCompanyMenu || showCommunityMenu || showUserMenu || showAdminMenu;

  useEffect(() => {
    if (!anyHeaderMenuOpen) return undefined;

    const handleMouseDown = (event) => {
      const refs = [companyMenuRef, communityMenuRef, userMenuRef, adminMenuRef];
      if (refs.some((r) => r.current?.contains(event.target))) return;
      closeHeaderMenus();
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closeHeaderMenus();
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [anyHeaderMenuOpen, closeHeaderMenus]);

  const openCompanyMenu = () => {
    setShowCommunityMenu(false);
    setShowUserMenu(false);
    setShowAdminMenu(false);
    setShowCompanyMenu((v) => !v);
  };

  const openCommunityMenu = () => {
    setShowCompanyMenu(false);
    setShowUserMenu(false);
    setShowAdminMenu(false);
    setShowCommunityMenu((v) => !v);
  };

  const openUserMenu = () => {
    setShowCompanyMenu(false);
    setShowCommunityMenu(false);
    setShowAdminMenu(false);
    setShowUserMenu((v) => !v);
  };

  const openAdminMenu = () => {
    setShowCompanyMenu(false);
    setShowCommunityMenu(false);
    setShowUserMenu(false);
    setShowAdminMenu((v) => !v);
  };

  const hubSection = useMemo(() => {
    const m = location.pathname.match(/\/hubup\/g\/[^/]+\/([^/]+)/);
    return m ? m[1] : 'dashboard';
  }, [location.pathname]);

  useEffect(() => {
    if (onHubupUrl && profile?.kind === 'dual' && activeApp !== 'hubup' && !chooserOpen) {
      chooseAppMode('hubup');
    }
  }, [onHubupUrl, profile?.kind, activeApp, chooseAppMode, chooserOpen]);

  useEffect(() => {
    if (inWebCreator && activeApp !== 'web-creator' && !chooserOpen) {
      chooseAppMode('web-creator');
    }
  }, [inWebCreator, activeApp, chooseAppMode, chooserOpen]);

  useEffect(() => {
    if (
      onEnterpriseUrl &&
      canOpenEnterpriseApp(currentUser) &&
      activeApp !== 'enterprise' &&
      !chooserOpen
    ) {
      chooseAppMode('enterprise');
    }
  }, [onEnterpriseUrl, currentUser, activeApp, chooseAppMode, chooserOpen]);

  const hubupNavigationItems = useMemo(() => {
    if (!groupId) return [];
    const base = `/hubup/g/${groupId}`;
    const items = [
      {
        label: 'Tableau de bord',
        path: `${base}/dashboard`,
        icon: 'LayoutDashboard',
      },
      {
        label: 'Mes rubriques',
        path: `${base}/subjects`,
        icon: 'FolderOpen',
      },
      {
        label: 'Mes notifications',
        path: `${base}/notifications`,
        icon: 'Bell',
      },
      {
        label: 'Annuaire',
        path: `${base}/form-annuaire`,
        icon: 'BookUser',
      },
    ];
    if (canManageHubupCommunitySettings(currentUser)) {
      items.push({
        label: 'Profil publique',
        path: `${base}/public-profile`,
        icon: 'Globe',
      });
    }
    return items;
  }, [groupId, currentUser]);

  const adminNavigationItems = useMemo(() => {
    if (inHubupShell) {
      if (!groupId) return [];
      const base = `/hubup/g/${groupId}`;
      return [
        {
          label: 'Gérer les communautés',
          path: `${base}/communities-manage`,
          icon: 'Settings2',
        },
        {
          label: 'Approbation annuaire',
          path: `${base}/annuary-approvals`,
          icon: 'ClipboardCheck',
        },
        {
          label: 'Gestion des comptes',
          path: `${base}/accounts-manage`,
          icon: 'UserCog',
        },
      ];
    }
    return [
      {
        label: 'Gérer les entreprises',
        path: '/companies-manage',
        icon: 'Building2',
      },
      {
        label: 'Ventes',
        path: '/sales-admin',
        icon: 'Receipt',
      },
      {
        label: 'Gestion des comptes',
        path: '/accounts-manage',
        icon: 'UserCog',
      },
    ];
  }, [inHubupShell, groupId]);

  const crmNavigationItems = useMemo(
    () => [
      {
        label: 'Performance',
        path: '/call-performance-analytics',
        icon: 'Phone',
      },
      {
        label: 'Tableau de bord',
        path: '/prospect-pipeline-monitor',
        icon: 'TrendingUp',
      },
      {
        label: 'Annuaire',
        path: '/prospect-contact-directory',
        icon: 'BookUser',
      },
      {
        label: 'Campagnes',
        path: '/campaigns',
        icon: 'Megaphone',
      },
      {
        label: 'Configuration',
        path: '/crm-configuration',
        icon: 'Settings',
        configOnly: true,
      },
      {
        label: 'Support',
        path: '/gallery',
        icon: 'Video',
      },
      {
        label: "Gestion d'équipe",
        path: '/team-management',
        icon: 'Users',
        teamLeadOnly: true,
      },
    ],
    [],
  );

  const navigationItems = inWebCreator
    ? []
    : onEnterpriseUrl
      ? [{ label: 'Entreprises', path: getDefaultEnterprisePath(), icon: 'Building2' }]
      : inHubupShell
        ? hubupNavigationItems
        : crmNavigationItems;

  const logoTarget = inWebCreator
    ? getDefaultWebCreatorPath()
    : onEnterpriseUrl
      ? getDefaultEnterprisePath()
      : inHubupShell
        ? groupId
          ? `/hubup/g/${groupId}/dashboard`
          : '/hubup'
        : '/call-performance-analytics';

  const brandTitle = inWebCreator
    ? 'Just One Page'
    : onEnterpriseUrl
      ? 'Entreprises'
      : inHubupShell
        ? 'HUBUP'
        : 'CRM BOOSTER';

  const logoIcon = 'BarChart3';

  const isActive = (path) => {
    if (path.endsWith('/accounts-manage')) {
      return (
        location?.pathname === path || location?.pathname === '/accounts-manage'
      );
    }
    return location?.pathname === path;
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleNavClick = () => {
    setIsMobileMenuOpen(false);
  };

  const getStatusColor = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'connected';
      case 'connecting':
        return 'connecting';
      case 'disconnected':
        return 'disconnected';
      default:
        return 'disconnected';
    }
  };

  const getStatusText = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'Connecté';
      case 'connecting':
        return 'Connexion...';
      case 'disconnected':
        return 'Déconnecté';
      default:
        return 'Déconnecté';
    }
  };

  const isAdmin = currentUser?.permission === 'admin';
  const hasAffiliateCode = Boolean(currentUser?.affiliateCode?.trim());

  const affiliateNavItem = hasAffiliateCode
    ? {
        label: 'Suivi affilié',
        path: '/affiliate-tracking',
        icon: 'BadgePercent',
      }
    : null;

  const filteredNavigation = inWebCreator
    ? []
    : [
        ...navigationItems.filter((item) => {
          if (item.configOnly) {
            if (isAdmin) return true;
            if (currentUser?.permission === 'team' && currentUser?.crm === true) return true;
            return false;
          }
          if (item.teamLeadOnly) {
            if (isAdmin) return false;
            if (currentUser?.permission !== 'team' || currentUser?.crm !== true) {
              return false;
            }
          }
          return true;
        }),
        ...(affiliateNavItem ? [affiliateNavItem] : []),
      ];

  const showAdminNav = isAdmin && adminNavigationItems.length > 0;

  const isAdminSectionActive = adminNavigationItems.some((item) =>
    isActive(item.path),
  );

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    window.location.href = '/login';
  };

  const showAppSwitch = hasMultipleApps(currentUser);
  const showCommunitySwitcher =
    inHubupShell && groupId && hubupCommunities.length > 0;

  const crmCompanies = companyCx?.companies ?? [];
  const showCompanySwitcher =
    !inWebCreator && !inHubupShell && crmCompanies.length > 0 && companyCx?.activeCompanyId;

  const onCompanySelect = (newId) => {
    companyCx?.setActiveCompanyId(newId);
    setShowCompanyMenu(false);
    window.location.reload();
  };

  const currentCompanyName =
    crmCompanies.find((c) => String(c._id) === String(companyCx?.activeCompanyId))
      ?.name ?? 'Entreprise';

  const onCommunitySelect = (newId) => {
    navigate(`/hubup/g/${newId}/${hubSection}`, { replace: true });
    setShowCommunityMenu(false);
  };

  const currentCommunityName =
    hubupCommunities.find((c) => c.id === groupId)?.name ?? 'Communauté';

  return (
    <>
      <header className="header-container">
        <div className="header-inner">
          <Link to={logoTarget} className="header-logo">
            {inWebCreator ? (
              <JustOnePageLogo size="sm" />
            ) : (
              <>
                <div className="header-logo-icon">
                  <Icon name={logoIcon} size={24} color="#FFFFFF" />
                </div>
                <span className="header-logo-text">{brandTitle}</span>
              </>
            )}
          </Link>

          {(filteredNavigation.length > 0 || showAdminNav) && (
          <nav className="header-nav">
            {filteredNavigation?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`header-nav-item ${isActive(item?.path) ? 'active' : ''}`}
              >
                {item?.label}
              </Link>
            ))}
            {showAdminNav ? (
              <div
                ref={adminMenuRef}
                className={`header-nav-dropdown-group relative ${
                  showAdminMenu ? 'is-open' : ''
                }`}
              >
                <button
                  type="button"
                  className={`header-nav-item inline-flex items-center gap-1 cursor-pointer select-none border-0 bg-transparent font-inherit ${
                    isAdminSectionActive ? 'active' : ''
                  }`}
                  aria-haspopup="true"
                  aria-expanded={showAdminMenu}
                  onClick={openAdminMenu}
                >
                  Administration
                  <Icon name="ChevronDown" size={14} className="opacity-80" />
                </button>
                <div className="header-nav-dropdown" role="menu">
                  <div>
                    {adminNavigationItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        role="menuitem"
                        className={`header-nav-dropdown-item ${
                          isActive(item.path) ? 'active' : ''
                        }`}
                        onClick={() => setShowAdminMenu(false)}
                      >
                        <Icon name={item.icon} size={16} className="shrink-0 opacity-80" />
                        <span>{item.label}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
          </nav>
          )}

          <div className="header-actions">
            <div
              className={`status-indicator flex items-center gap-2 flex-wrap justify-end${
                inWebCreator ? '' : ' hidden md:flex'
              }`}
            >
              {showCompanySwitcher && (
                <div ref={companyMenuRef} className="relative">
                  <button
                    type="button"
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-border bg-background hover:bg-muted/80 text-xs text-foreground max-w-[200px] transition-smooth"
                    onClick={openCompanyMenu}
                    aria-expanded={showCompanyMenu}
                  >
                    <Icon name="Building2" size={14} className="shrink-0" />
                    <span className="truncate">{currentCompanyName}</span>
                    <Icon name="ChevronDown" size={14} className="shrink-0 opacity-70" />
                  </button>
                  {showCompanyMenu && (
                    <div className="absolute right-0 mt-1.5 w-56 max-h-64 overflow-y-auto bg-popover border border-border rounded-md shadow-elevation-3 py-1 z-50">
                      {crmCompanies.map((c) => (
                        <button
                          key={c._id}
                          type="button"
                          className={`w-full text-left px-3 py-2 text-xs hover:bg-muted transition-smooth ${
                            String(c._id) === String(companyCx?.activeCompanyId)
                              ? 'bg-muted/60 font-medium'
                              : ''
                          }`}
                          onClick={() => onCompanySelect(c._id)}
                        >
                          {c.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {showCommunitySwitcher && (
                <div ref={communityMenuRef} className="relative">
                  <button
                    type="button"
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-border bg-background hover:bg-muted/80 text-xs text-foreground max-w-[200px] transition-smooth"
                    onClick={openCommunityMenu}
                    aria-expanded={showCommunityMenu}
                  >
                    <Icon name="UsersRound" size={14} className="shrink-0" />
                    <span className="truncate">{currentCommunityName}</span>
                    <Icon name="ChevronDown" size={14} className="shrink-0 opacity-70" />
                  </button>
                  {showCommunityMenu && (
                    <div className="absolute right-0 mt-1.5 w-56 max-h-72 overflow-hidden flex flex-col bg-popover border border-border rounded-md shadow-elevation-3 z-50">
                      <div className="shrink-0 border-b border-border p-2">
                        <div className="flex items-center gap-2 px-2 py-1.5 rounded-md border border-border bg-background">
                          <Icon
                            name="Search"
                            size={14}
                            className="shrink-0 text-muted-foreground"
                          />
                          <input
                            ref={communitySearchRef}
                            type="search"
                            value={communitySearch}
                            onChange={(e) => setCommunitySearch(e.target.value)}
                            placeholder="Rechercher une communauté…"
                            className="flex-1 min-w-0 bg-transparent text-xs text-foreground placeholder:text-muted-foreground outline-none"
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                          />
                        </div>
                      </div>
                      <div className="overflow-y-auto py-1 max-h-52">
                        {filteredHubupCommunities.length === 0 ? (
                          <p className="px-3 py-2 text-xs text-muted-foreground">
                            Aucune communauté trouvée.
                          </p>
                        ) : (
                          filteredHubupCommunities.map((c) => (
                            <button
                              key={c.id}
                              type="button"
                              className={`w-full text-left px-3 py-2 text-xs hover:bg-muted transition-smooth ${
                                c.id === groupId ? 'bg-muted/60 font-medium' : ''
                              }`}
                              onClick={() => onCommunitySelect(c.id)}
                            >
                              {c.name}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {!inWebCreator && (
                <>
                  <span className={`status-dot ${getStatusColor()}`}></span>
                  <span className="status-text">{getStatusText()}</span>
                </>
              )}

              {currentUser && (
                <div ref={userMenuRef} className="relative">
                  <button
                    type="button"
                    className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted hover:bg-muted/80 text-xs text-foreground transition-smooth"
                    onClick={openUserMenu}
                    aria-expanded={showUserMenu}
                  >
                    <Icon name="User" size={14} />
                    <span className="max-w-[180px] truncate">{currentUser.email}</span>
                  </button>
                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-52 bg-popover border border-border rounded-md shadow-elevation-3 py-1 z-50">
                      {showAppSwitch && (
                        <button
                          type="button"
                          className="w-full flex items-center gap-2 px-3 py-2 text-xs text-foreground hover:bg-muted transition-smooth"
                          onClick={() => {
                            setShowUserMenu(false);
                            openAppChooser();
                          }}
                        >
                          <Icon name="Shuffle" size={14} />
                          <span>Changer d&apos;application</span>
                        </button>
                      )}
                      {!inWebCreator && (
                        <button
                          type="button"
                          className="w-full flex items-center gap-2 px-3 py-2 text-xs text-foreground hover:bg-muted transition-smooth"
                          onClick={handleLogout}
                        >
                          <Icon name="LogOut" size={14} />
                          <span>Déconnexion</span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
      {(!inWebCreator || showAdminNav || showAppSwitch) && (
      <button
        className="mobile-menu-button"
        onClick={toggleMobileMenu}
        aria-label="Toggle mobile menu"
        aria-expanded={isMobileMenuOpen}
      >
        <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
      </button>
      )}
      {isMobileMenuOpen && (
        <div className="mobile-menu-overlay">
          <nav className="mobile-menu-content">
            {filteredNavigation?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`mobile-menu-item ${isActive(item?.path) ? 'active' : ''}`}
                onClick={handleNavClick}
              >
                <Icon name={item?.icon} size={20} />
                <span>{item?.label}</span>
              </Link>
            ))}
            {showAdminNav ? (
              <div className="mobile-menu-admin-section">
                <p className="mobile-menu-admin-heading">Administration</p>
                {adminNavigationItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`mobile-menu-item ${isActive(item.path) ? 'active' : ''}`}
                    onClick={handleNavClick}
                  >
                    <Icon name={item.icon} size={20} />
                    <span>{item.label}</span>
                  </Link>
                ))}
              </div>
            ) : null}
            {showAppSwitch && (
              <button
                type="button"
                className="mobile-menu-item text-left w-full border-0 bg-transparent"
                onClick={() => {
                  handleNavClick();
                  openAppChooser();
                }}
              >
                <Icon name="Shuffle" size={20} />
                <span>Changer d&apos;application</span>
              </button>
            )}
            <div className="mobile-menu-item mt-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between border-t border-border pt-4">
              {showCommunitySwitcher && (
                <div className="flex flex-col gap-1.5 flex-1 min-w-0 w-full sm:max-w-[min(100%,16rem)]">
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    Communauté
                  </span>
                  <div className="flex items-center gap-2 px-2 py-1.5 rounded-md border border-border bg-background">
                    <Icon
                      name="Search"
                      size={14}
                      className="shrink-0 text-muted-foreground"
                    />
                    <input
                      type="search"
                      value={communitySearch}
                      onChange={(e) => setCommunitySearch(e.target.value)}
                      placeholder="Rechercher…"
                      className="flex-1 min-w-0 bg-transparent text-xs outline-none"
                    />
                  </div>
                  <select
                    className="w-full rounded-md border border-input bg-background px-2 py-2 text-xs"
                    value={groupId}
                    onChange={(e) => {
                      onCommunitySelect(e.target.value);
                      handleNavClick();
                    }}
                  >
                    {(communitySearch.trim()
                      ? filteredHubupCommunities
                      : hubupCommunities
                    ).map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {!inWebCreator && (
                <div className="flex items-center gap-2 shrink-0 sm:ml-auto">
                  <span className={`status-dot ${getStatusColor()}`}></span>
                  <span>{getStatusText()}</span>
                </div>
              )}
            </div>
          </nav>
        </div>
      )}
    </>
  );
};

export default Header;
